// EP-150 (App Review 5.1.1v): the account deletion flow.
// docs/ep-150-app-store-review.md §3 is the contract it renders.
//
// Rendered by the last Settings tab (owner decision 2026-09-29: the entry
// point moved there from the user menu) and by the /account/delete page. The
// caller supplies any heading above the card; the panel does its own loading,
// the explanation, the password and the confirmation.
import { useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Trash2 } from 'lucide-react'
import { Card, Alert, Input, Button, ConfirmDialog, Skeleton } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useAccountDeletionStatus, useRequestAccountDeletion } from '@/hooks/useAccountDeletion'
import { useMaskedPasswordField } from '@/hooks/useMaskedPasswordField'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localDateDaysAhead } from '@/lib/date'
import { saveDeletionNotice } from '@/lib/deletionNotice'

export function AccountDeletionPanel({ header }: { header?: ReactNode }) {
  const { t } = useTranslation('account')
  const { clearSession } = useAuth()
  const navigate = useNavigate()

  const formatDate = useDateFormatter()
  const { timezone, format: dateFormat, calendar } = useLocalizationConfig()
  const translateError = useApiErrorTranslator('account')

  const { data, isLoading, isError } = useAccountDeletionStatus()
  const requestDeletion = useRequestAccountDeletion()

  const passwordRef = useRef<HTMLInputElement>(null)
  // The confirm button is disabled while the request runs, but only from the
  // next render. Two clicks inside one frame would both get through.
  const submittingRef = useRef(false)
  const { showPassword, toggle: togglePassword, type: passwordType, style: passwordStyle } =
    useMaskedPasswordField()
  const [passwordError, setPasswordError] = useState('')
  const [formError, setFormError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isAccountScope = data?.scope === 'account'

  // The server hasn't stamped a real `scheduled_at` before a request is
  // filed (status is always "none" here), so preview today + grace_days in
  // the account's own zone; after a successful request the confirmation
  // instead carries the server's own `scheduled_at` (see handleConfirm below).
  const previewDate =
    data != null ? formatDate(localDateDaysAhead(data.grace_days, timezone)) : ''

  const openConfirm = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    const password = passwordRef.current?.value ?? ''
    if (!password) {
      setPasswordError(t('delete.password_required'))
      return
    }
    setPasswordError('')
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    const password = passwordRef.current?.value ?? ''
    try {
      const result = await requestDeletion.mutateAsync({ password })
      setConfirmOpen(false)
      // Saved before the session is cleared: clearing it makes
      // ProtectedRoute redirect to /login by itself, and the sign-in page
      // reads the notice whichever navigation lands.
      saveDeletionNotice({
        scope: result.scope,
        scheduledAt: result.scheduled_at,
        display: { timezone, date_format: dateFormat, calendar },
      })
      // The server already revoked this session's tokens the moment the
      // request succeeded. clearSession only mirrors that locally (tokens +
      // the React Query cache), with none of logout()'s network calls. See
      // the comment on AuthContextType#clearSession.
      clearSession()
      navigate('/login', { replace: true })
    } catch (err) {
      setConfirmOpen(false)
      const code = (err as { response?: { data?: { error?: { code?: string } } } }).response?.data
        ?.error?.code
      if (code === 'password_invalid') {
        setPasswordError(t('delete.errors.password_invalid'))
      } else {
        setFormError(translateError(err))
      }
    } finally {
      submittingRef.current = false
    }
  }

  return (
    <>
      {isLoading && (
        <Card>
          <div className="space-y-3" data-testid="account-delete-loading">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </Card>
      )}

      {isError && (
        <Alert variant="error" data-testid="account-delete-load-error">
          {t('delete.load_error')}
        </Alert>
      )}

      {data && (
        <>
          {header}

          <Card>
            <div className="space-y-6">
              <section>
                <h2 className="font-semibold mb-1">{t('delete.what_heading')}</h2>
                <p className="text-sm text-base-content/80">
                  {isAccountScope ? t('delete.what_account') : t('delete.what_user')}
                </p>
              </section>

              <section>
                <h2 className="font-semibold mb-1">{t('delete.when_heading')}</h2>
                <p className="text-sm text-base-content/80" data-testid="account-delete-when">
                  {t('delete.when_body', { date: previewDate })}
                </p>
              </section>

              <section>
                <h2 className="font-semibold mb-1">{t('delete.restore_heading')}</h2>
                <p className="text-sm text-base-content/80">
                  {t('delete.restore_body', { days: data.grace_days })}
                </p>
                {isAccountScope && (
                  <p className="text-sm text-base-content/80">{t('delete.restore_owner_only')}</p>
                )}
              </section>

              {isAccountScope && (
                <section data-testid="account-delete-kept">
                  <h2 className="font-semibold mb-1">{t('delete.kept_heading')}</h2>
                  <p className="text-sm text-base-content/80">{t('delete.kept_body')}</p>
                </section>
              )}

              {isAccountScope && (
                <Alert variant="info" icon={null}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span>{t('delete.closing_package_suggestion')}</span>
                    <Link to="/closing-package" className="link link-primary text-sm font-medium">
                      {t('delete.closing_package_link')}
                    </Link>
                  </div>
                </Alert>
              )}

              {formError && <Alert variant="error">{formError}</Alert>}

              <form onSubmit={openConfirm} className="space-y-4">
                <div className="max-w-sm">
                  {/* The error sits outside this box. The toggle is pinned to
                      the box's bottom edge, and an error inside it would push
                      that edge, and the toggle, below the field. */}
                  <div className="relative">
                    <Input
                      id="account-delete-password"
                      data-testid="account-delete-password"
                      label={t('delete.password_label')}
                      type={passwordType}
                      style={passwordStyle}
                      inputClassName={`pr-10 ${passwordError ? 'input-error' : ''}`}
                      placeholder={t('delete.password_placeholder')}
                      ref={passwordRef}
                      defaultValue=""
                      aria-invalid={passwordError ? true : undefined}
                      aria-describedby={passwordError ? 'account-delete-password-error' : undefined}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      data-testid="account-delete-password-toggle"
                      onClick={togglePassword}
                      aria-label={t('delete.toggle_password_visibility')}
                      className="absolute right-3 bottom-0 h-10 flex items-center text-base-content/50 hover:text-base-content"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="account-delete-password-error" className="label-text-alt text-error px-1 pt-1">
                      {passwordError}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="error"
                  leftIcon={<Trash2 className="w-4 h-4" />}
                  data-testid="account-delete-submit"
                >
                  {isAccountScope ? t('delete.submit_account') : t('delete.submit_user')}
                </Button>
              </form>
            </div>
          </Card>

          <ConfirmDialog
            isOpen={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleConfirm}
            title={isAccountScope ? t('delete.confirm_title_account') : t('delete.confirm_title_user')}
            message={
              isAccountScope
                ? t('delete.confirm_message_account', { days: data.grace_days })
                : t('delete.confirm_message_user', { days: data.grace_days })
            }
            confirmLabel={t('delete.confirm_confirm')}
            cancelLabel={t('delete.confirm_cancel')}
            variant="error"
            loading={requestDeletion.isPending}
          />
        </>
      )}
    </>
  )
}
